import React from 'react';

import {BlockViewer} from './BlockViewer';
import {registerBlocks} from './BlockSupport';
// @ts-ignore
import {Parser as HtmlToReactParser} from 'html-to-react';
import {Column} from './library/Column';
import {Columns} from './library/Columns';
import {BlockInstance} from '@wordpress/blocks';
import {Group} from './library/Group';

const {useEffect, useMemo, useState} = React;

const htmlToReactParser = new HtmlToReactParser();

class ErrorBoundary extends React.Component<{}, { hasError: boolean }> {
    state = {
        hasError: false,
    };

    static getDerivedStateFromError(error: Error) {
        return {hasError: true};
    }

    componentDidCatch(error: Error, errorInfo: React.ErrorInfo) {
        // call error logging or reporting service
        console.error(error, errorInfo);
        this.setState(ErrorBoundary.getDerivedStateFromError(error));
    }

    render() {
        if (this.state.hasError) {
            return <></>;
        }
        return this.props.children;
    }
}

interface BlockRender {
    clientId: string;
    attributes: { [key: string]: any };
    innerBlocks: BlockRender[];
    isValid: boolean;
    name: string;
    originalContent: string;
}

export const BlockRenderContext = React.createContext<BlockRender | null>(null);

export const RenderGenericSimpleBlock = ({block}: {block: BlockInstance}) => {
    return useMemo(() => htmlToReactParser.parse(window.wp.blocks.serialize([block])), [block]);
}

function getRenderingHandler(block: BlockRender) {
    let name = block.name;
    switch (name) {
        case "core/column":
            return Column;
        case "core/columns":
            return Columns;
        case "core/group":
            return Group;
        default:
            if(block.innerBlocks && block.innerBlocks.length > 0) {
                return () => <div>Error: block {name} is not supported for frontend rendering</div>
            }
            return () => <RenderGenericSimpleBlock block={block}/>
    }
}

export function RenderBlock({block}: { block: BlockRender }) {
    const [Component, setComponent] = useState<any>({value: undefined});
    const [loaded, setLoaded] = useState<boolean>(false);
    useEffect(() => {
        let componentLoader = window['smartfire'].components[block.name.split('/')[1]];
        if (componentLoader) {
            (async () => {
                const C = await componentLoader();
                setComponent({value: BlockViewer({block: C})});
                setLoaded(true);
            })();
        } else {
            setComponent({value: getRenderingHandler(block)});
            setLoaded(true);
        }
    }, [block]);
    if (!loaded) return null;
    const props = block.attributes;
    return <BlockRenderContext.Provider value={block}>
        <ErrorBoundary>
            <Component.value {...props}/>
        </ErrorBoundary>
    </BlockRenderContext.Provider>;
}

export function RenderPage({content}: { content: string }) {
    const parsed = useMemo<any[]>(() => {
        if (content) {
            // @ts-ignore
            return window.wp.blocks.parse(content);
        }
        return [];
    }, [content]);
    return <ErrorBoundary>
        {parsed.map((b) => <RenderBlock key={b.clientId} block={b}/>)}
    </ErrorBoundary>;
}

registerBlocks();

export default RenderPage;
